import React, { useState, useEffect } from 'react';
import { User } from '../../services/authService';
import { passwordService, PasswordEntry } from '../../services/passwordService';
import PasswordList from './PasswordList';
import PasswordForm from './PasswordForm';
import PageHeader from '../common/PageHeader';
import './Password.css';

interface PasswordPageProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

const PasswordPage: React.FC<PasswordPageProps> = ({ user, onToggleSidebar }) => {
  const [passwords, setPasswords] = useState<PasswordEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState<PasswordEntry | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (user) {
      loadPasswords();
    }
  }, [user]);

  const loadPasswords = async () => {
    try {
      setLoading(true);
      const response = await passwordService.getPasswords({
        search: searchQuery || undefined,
        limit: 100
      });

      if (response.success && response.passwords) {
        setPasswords(response.passwords);
      }
    } catch (error) {
      console.error('Error loading passwords:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Debounce search
    const timer = setTimeout(() => {
      loadPasswords();
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleAddClick = () => {
    setEditingEntry(null);
    setShowForm(true);
  };

  const handleEditClick = async (entry: PasswordEntry) => {
    try {
      // Fetch full entry with password for editing
      const response = await passwordService.getPasswordById(entry.id);
      if (response.success && response.password) {
        setEditingEntry(response.password);
        setShowForm(true);
      } else {
        alert('Failed to load password for editing');
      }
    } catch (error) {
      console.error('Error loading password for editing:', error);
      alert('Failed to load password for editing');
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingEntry(null);
    loadPasswords();
  };

  const handleDeleteClick = async (id: string) => {
    try {
      await passwordService.deletePassword(id);
      loadPasswords();
    } catch (error) {
      console.error('Error deleting password:', error);
      alert('Failed to delete password');
    }
  };

  return (
    <div className="password-page">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={
          <h1 className="password-page-title">/passwords</h1>
        }
        rightActions={
          <>
            <input
              type="text"
              className="password-search-input"
              placeholder="Search passwords..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button className="password-add-btn" onClick={handleAddClick}>
              + Add Password
            </button>
          </>
        }
      />

      <div className="password-page-content">
        {loading ? (
          <div className="password-loading">Loading...</div>
        ) : (
          <PasswordList
            user={user}
            passwords={passwords}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
            refreshList={loadPasswords}
          />
        )}
      </div>

      {showForm && (
        <PasswordForm
          user={user}
          entry={editingEntry}
          onClose={handleFormClose}
        />
      )}
    </div>
  );
};

export default PasswordPage;

